<template>
  <div v-if="mediaErrorMessage" class="margin-top-xxs media-error-container margin-bottom-xs padding-xs">
    <div class="show-for-sr" aria-live="polite">
      <span>{{ mediaErrorMessage }}</span>
    </div>
    <div aria-live="polite" class="small form-error media-error" aria-hidden="true">
      <div class="error-icon error-red-medium margin-right-xxs" role="graphics-symbol" aria-label="Error"></div>
      <span class="error-message-body">{{ mediaErrorMessage }}</span>
      <button class="close-dark-small" @click="removeError"></button>
    </div>
  </div>
  <div class="media-upload grid-x">
    <div class="media-blank-container cell grid-x grid-margin-x">
      <div v-for="n in 3" :key="n" class="media-placeholder media-common-style cell small-4 medium-2"></div>
    </div>
    <div class="media-upload-container cell grid-x grid-margin-x">
      <div v-for="(image, index) in images" :key="index" class="uploaded-media media-common-style cell small-4 medium-2">
        <div
          id="img"
          class="media-common-style"
          :class="{ 'review-mcom': isMcom }"
          :style="`background-image: url(${image.url});`"
          role="img"
          :aria-label="`Uploaded image #${index + 1} of ${PHOTO_LIMITS.MAX_PHOTOS_COUNT}`"
        ></div>
        <button type="button" class="close-btn-review" :class="removeImgBtnClassBindings" @click="removePhoto(index)"></button>
      </div>
      <div v-if="isLoading" class="media-placeholder media-common-style cell small-4 medium-2">
        <Spinner />
      </div>
      <label
        v-if="!hidePhotoInput"
        for="img_upload"
        class="cell small-4 medium-2"
        :class="{ 'is-loading': isLoading }"
        aria-label="Upload photos for your review"
      >
        <div class="upload-media-button media-common-style primary" role="button">
          <span class="camera-dark-medium"></span>
          <input
            id="img_upload"
            type="file"
            name="img_upload"
            :accept="ACCEPTABLE_IMG_FORMATS_INPUT"
            multiple
            :disabled="isLoading"
            @change="onFilesSelected"
          />
        </div>
      </label>
    </div>
    <div class="media-helper-subtext small margin-top-xxs">
      <p>
        Add up to 3 photos <span v-if="remainingPhotos < 3">({{ remainingPhotos }} remaining)</span>
      </p>
      <p>File size must be 5 MB or less</p>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed } from 'vue';
import Spinner from '@atomic-ui/spinner/Spinner';
import { PHOTO_LIMITS } from '~/modules/reviews/components/writeReview/constants';
import { useProduct } from '~/modules/product/composables/useProduct';
import useReviewImageUpload from '~/modules/reviews/composables/useReviewImageUpload';
import { useBrand } from '~/composables/useBrand';

const { productId } = useProduct();
const emit = defineEmits(['update-images']);

const { isMcom } = useBrand();

const removeImgBtnClassBindings = {
  'close-dark-large': isMcom,
  'review-mcom': isMcom,
  'close-dark-medium': !isMcom,
  'review-bcom': !isMcom,
};

const { images, isLoading, mediaErrorMessage, uploadPhotos, removePhoto, validatePhotos } = useReviewImageUpload({ productId });

const ACCEPTABLE_IMG_FORMATS = ['png', 'jpeg', 'jpg', 'gif', 'tif', 'tiff', 'heic'];
const ACCEPTABLE_IMG_FORMATS_INPUT = ACCEPTABLE_IMG_FORMATS.map((type) => `.${type}`).join(', ');

const tempLoading = ref(0);
const remainingPhotos = ref(3);

const hidePhotoInput = computed(() => images.value.length + tempLoading.value >= 3);
watch(isLoading, (v) => {
  tempLoading.value = v ? 1 : 0;
});

watch(
  () => images.value.length,
  (newLength, oldLength) => {
    if (newLength < oldLength) {
      remainingPhotos.value += 1;
      mediaErrorMessage.value = '';
    }
    if (newLength > oldLength) {
      remainingPhotos.value -= newLength - oldLength;
    }
    emit('update-images', images.value);
  },
);

const onFilesSelected = async (/** @type {{ target: { files: object[]; }; }} */ event) => {
  mediaErrorMessage.value = '';
  if (event.target.files.length > 0) {
    if (images.value.length + event.target.files.length <= PHOTO_LIMITS.MAX_PHOTOS_COUNT) {
      await validatePhotos(event.target.files);
      if (!mediaErrorMessage.value) {
        await uploadPhotos(event.target.files);
      }
    } else {
      mediaErrorMessage.value = 'Total allowed images for upload cannot exceed 3.';
    }
  }

  event.target.value = null;
};

function removeError() {
  if (mediaErrorMessage.value) mediaErrorMessage.value = '';
}
</script>

<style brand="mcom" lang="scss" scoped>
@import '~@csgIcons/new/close-dark-large';
</style>
<style brand="bcom" lang="scss" scoped>
@import '~@csgIcons/new/close-dark-medium';
</style>

<style lang="scss" scoped>
@import '@core/csg/src/scss/forms/forms.scss';
@import '~@csgIcons/new/camera-dark-medium';
@import '~@csgIcons/new/error-red-medium';
@import '~@csgIcons/new/close-dark-small';

@mixin media-common {
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #fff;
  aspect-ratio: 1/1;
  border-width: 1px;
  @include breakpoint(large) {
    min-width: rem-calc(98);
  }
}
.media-error-container {
  border: rem-calc(1) solid $error-color;
  .media-error {
    display: flex;
    align-items: flex-start;
    color: $error-color;
    .error-icon {
      flex-shrink: 0;
    }
  }
  .error-message-body {
    align-self: center;
    flex-grow: 1;
  }
  .close-dark-small {
    flex-shrink: 0;
    cursor: pointer;
  }
}
.media-upload {
  position: relative;
  label {
    cursor: pointer;
    &.is-loading {
      cursor: wait;
    }
    .upload-media-button.primary {
      @include media-common;
      border-style: solid;
      border-color: $black;
      flex-direction: column;
      &:focus-within {
        border-width: 2px;
      }
    }
  }
  .media-blank-container {
    position: absolute;

    @include breakpoint(small only) {
      margin: 0;
      justify-content: space-between;
    }
  }
  .media-upload-container {
    z-index: 10;

    @include breakpoint(small only) {
      margin-left: 0;
    }
  }
  .media-placeholder {
    @include media-common;
    border-style: dashed;
    border-color: $gray-1-color;
    position: relative;
  }

  .uploaded-media {
    @include media-common;
    border-style: solid;
    border-color: $gray-2-color;
    position: relative;
    .review-mcom {
      border-radius: 0.25rem;
    }

    #img {
      width: 100%;
      height: 100%;
      background-size: cover;
    }

    .review-bcom {
      background-color: #f9f9f9;
    }

    .close-btn-review {
      position: absolute;
      top: 0.5rem;
      right: 0.5rem;
      border-radius: 50%;
      cursor: pointer;
      opacity: 0.8;
    }

    .close-btn-review:hover {
      opacity: 0.9;
    }
  }

  input[type='file'] {
    position: absolute;
    opacity: 0;
    width: 1px;
    height: 1px;
    margin: 0;
  }

  .media-helper-subtext {
    color: $gray-1-color;
  }
}
</style>

<style lang="scss" scoped brand="mcom">
.guidelines {
  text-transform: capitalize;
}
.media-error-container,
.media-common-style {
  border-radius: rem-calc(5);
}
</style>
